import { useTranslation } from "react-i18next";
import {
  LightningBoltIcon,
  ShieldCheckIcon,
  ChipIcon,
  CloudIcon,
  CodeIcon,
  SupportIcon,
} from "@heroicons/react/outline";
import { EyeIcon, SparklesIcon } from "@heroicons/react/solid";

const serviceIcons = [CodeIcon, CloudIcon, LightningBoltIcon, ChipIcon, SupportIcon];

const gradients = [
  "linear-gradient(135deg, #7C3AED, #4F46E5)",
  "linear-gradient(135deg, #4F46E5, #06B6D4)",
  "linear-gradient(135deg, #06B6D4, #7C3AED)",
  "linear-gradient(135deg, #7C3AED, #22D3EE)",
];

export default function Home() {
  const { t } = useTranslation();

  const services = t("Services.items", { returnObjects: true });
  const team     = t("Team.members",   { returnObjects: true });
  const values   = t("Pillars.values", { returnObjects: true });
  const missionTags = t("Pillars.mission.tags", { returnObjects: true });
  const visionTags  = t("Pillars.vision.tags",  { returnObjects: true });
  const aboutTags   = t("About.tags",            { returnObjects: true });

  const navLinks = [
    { name: t("NavBar.Home"),    href: "#inicio" },
    { name: t("NavBar.About"),   href: "#nosotros" },
    { name: t("NavBar.Mission"), href: "#mision" },
    { name: t("NavBar.Vision"),  href: "#vision" },
    { name: t("NavBar.Team"),    href: "#equipo" },
    { name: t("NavBar.Contact"), href: "#contacto" },
  ];

  // Sin backend: el formulario abre el cliente de correo del visitante con el mensaje ya armado
  const handleContactSubmit = (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const subject = [data.service, data.name, data.company].filter(Boolean).join(" · ");
    const body = [
      `${t("Contact.form.name")}: ${data.name}`,
      `${t("Contact.form.company")}: ${data.company}`,
      `${t("Contact.form.email")}: ${data.email}`,
      `${t("Contact.form.service")}: ${data.service}`,
      "",
      data.message,
    ].join("\r\n");
    window.location.href =
      `mailto:info@sis-gt.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="bg-brand-dark text-white overflow-x-hidden">

      {/* ── INICIO ─────────────────────────────────────────────── */}
      <section
        id="inicio"
        className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-20 px-4 overflow-hidden"
      >
        <div className="absolute inset-0 bg-brand-dark">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(ellipse 80% 60% at 50% 0%, #7C3AED55, transparent), radial-gradient(ellipse 60% 40% at 80% 80%, #06B6D444, transparent)",
            }}
          />
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <div
          className="absolute top-1/4 -left-32 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #7C3AED, transparent)" }}
        />
        <div
          className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #06B6D4, transparent)" }}
        />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <img src="/img/logo.png" alt="SIS-GT Tecnología" className="h-20 w-auto" />
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6">
            <SparklesIcon className="h-4 w-4 text-cyan-400" />
            {t("Hero.badge")}
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight tracking-tight mb-6">
            {t("Hero.title1")}{" "}
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #4F46E5, #06B6D4)" }}
            >
              {t("Hero.titleGradient")}
            </span>{" "}
            {t("Hero.title2")}
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t("Hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#nosotros"
              className="px-8 py-3.5 rounded-full font-semibold text-white shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 hover:opacity-90 transition-all"
              style={{ background: "linear-gradient(135deg, #7C3AED, #4F46E5, #06B6D4)" }}
            >
              {t("Hero.cta1")}
            </a>
            <a
              href="#equipo"
              className="px-8 py-3.5 rounded-full font-semibold text-gray-300 border border-white/20 hover:border-cyan-400/50 hover:text-cyan-400 transition-all"
            >
              {t("Hero.cta2")}
            </a>
          </div>

          <div className="mt-20 grid grid-cols-3 gap-8 max-w-lg mx-auto">
            {[
              { value: "100+", label: t("Hero.stats.projects") },
              { value: "70+", label: t("Hero.stats.clients") },
              { value: "12+", label: t("Hero.stats.experience") },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div
                  className="text-3xl font-bold text-transparent bg-clip-text"
                  style={{ backgroundImage: "linear-gradient(90deg, #A78BFA, #22D3EE)" }}
                >
                  {stat.value}
                </div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-50">
          <span className="text-xs text-gray-500">{t("Hero.scroll")}</span>
          <div className="w-px h-8 bg-gradient-to-b from-gray-500 to-transparent" />
        </div>
      </section>

      {/* ── NOSOTROS ───────────────────────────────────────────── */}
      <section id="nosotros" className="py-24 px-4 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-cyan-400 mb-3">
                {t("About.label")}
              </p>
              <h2 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-6">
                {t("About.title1")}{" "}
                <span
                  className="text-transparent bg-clip-text"
                  style={{ backgroundImage: "linear-gradient(90deg, #A78BFA, #22D3EE)" }}
                >
                  {t("About.titleGradient")}
                </span>{" "}
                {t("About.title2")}
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">{t("About.p1")}</p>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">{t("About.p2")}</p>
              <div className="flex flex-wrap gap-3">
                {Array.isArray(aboutTags) && aboutTags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-1.5 rounded-full text-sm font-medium border border-violet-500/30 bg-violet-500/10 text-violet-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {Array.isArray(services) && services.map((service, i) => {
                const Icon = serviceIcons[i];
                return (
                  <div
                    key={service.name}
                    className="p-5 rounded-2xl border border-white/10 bg-white/5 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all group"
                  >
                    <div
                      className="inline-flex p-2 rounded-lg mb-3"
                      style={{ background: "linear-gradient(135deg, #7C3AED22, #06B6D422)" }}
                    >
                      <Icon className="h-5 w-5 text-cyan-400 group-hover:text-violet-400 transition-colors" aria-hidden="true" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-200 mb-1">{service.name}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{service.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── MISIÓN & VISIÓN ────────────────────────────────────── */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-widest uppercase text-cyan-400 mb-3">
              {t("Pillars.label")}
            </p>
            <h2 className="text-4xl sm:text-5xl font-extrabold">{t("Pillars.title")}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* MISIÓN */}
            <div id="mision" className="relative rounded-3xl overflow-hidden border border-white/10 p-px">
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(135deg, #7C3AED40, #4F46E540, #06B6D410)" }}
              />
              <div className="relative rounded-3xl bg-brand-dark/80 p-10 h-full">
                <div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6"
                  style={{ background: "linear-gradient(135deg, #7C3AED, #4F46E5)" }}
                >
                  <LightningBoltIcon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{t("Pillars.mission.title")}</h3>
                <p className="text-gray-400 text-lg leading-relaxed">{t("Pillars.mission.text")}</p>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex gap-6">
                    {Array.isArray(missionTags) && missionTags.map((v) => (
                      <div key={v} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                        <span className="text-sm text-gray-400">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* VISIÓN */}
            <div id="vision" className="relative rounded-3xl overflow-hidden border border-white/10 p-px">
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(135deg, #06B6D410, #4F46E540, #7C3AED40)" }}
              />
              <div className="relative rounded-3xl bg-brand-dark/80 p-10 h-full">
                <div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6"
                  style={{ background: "linear-gradient(135deg, #06B6D4, #4F46E5)" }}
                >
                  <EyeIcon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{t("Pillars.vision.title")}</h3>
                <p className="text-gray-400 text-lg leading-relaxed">{t("Pillars.vision.text")}</p>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex gap-6">
                    {Array.isArray(visionTags) && visionTags.map((v) => (
                      <div key={v} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                        <span className="text-sm text-gray-400">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Valores */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Array.isArray(values) && values.map((valor) => (
              <div
                key={valor.title}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center hover:border-cyan-500/30 transition-all"
              >
                <h4 className="font-bold text-gray-200 mb-1">{valor.title}</h4>
                <p className="text-xs text-gray-500">{valor.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EQUIPO ─────────────────────────────────────────────── */}
      <section id="equipo" className="py-24 px-4 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-widest uppercase text-cyan-400 mb-3">
              {t("Team.label")}
            </p>
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              {t("Team.title1")}{" "}
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #A78BFA, #22D3EE)" }}
              >
                SIS-GT
              </span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto text-lg">{t("Team.subtitle")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.isArray(team) && team.map((member, i) => (
              <div
                key={member.name}
                className="group relative rounded-3xl border border-white/10 bg-white/5 p-6 hover:border-violet-500/40 transition-all hover:-translate-y-1"
              >
                <div className="flex flex-col items-center text-center">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold text-white mb-4 shadow-lg"
                    style={{ background: gradients[i % gradients.length] }}
                  >
                    {member.initials}
                  </div>
                  <h3 className="text-lg font-bold text-white">{member.name}</h3>
                  <p className="text-sm font-medium text-cyan-400 mt-0.5 mb-3">{member.role}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTÁCTANOS ────────────────────────────────────────── */}
      <section id="contacto" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-widest uppercase text-cyan-400 mb-3">
              {t("Contact.label")}
            </p>
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              {t("Contact.title1")}{" "}
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #A78BFA, #22D3EE)" }}
              >
                {t("Contact.titleGradient")}
              </span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto text-lg">{t("Contact.subtitle")}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Formulario */}
            <div className="relative rounded-3xl border border-white/10 bg-white/5 p-8">
              <form className="space-y-6" onSubmit={handleContactSubmit}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t("Contact.form.name")}
                    </label>
                    <input
                      name="name"
                      required
                      type="text"
                      placeholder={t("Contact.form.namePlaceholder")}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/40 transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t("Contact.form.company")}
                    </label>
                    <input
                      name="company"
                      type="text"
                      placeholder={t("Contact.form.companyPlaceholder")}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/40 transition-colors text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t("Contact.form.email")}
                  </label>
                  <input
                    name="email"
                    required
                    type="email"
                    placeholder={t("Contact.form.emailPlaceholder")}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/40 transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t("Contact.form.service")}
                  </label>
                  <select name="service" className="w-full px-4 py-3 rounded-xl bg-[#0B1120] border border-white/10 text-gray-300 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/40 transition-colors text-sm">
                    <option value="">{t("Contact.form.serviceDefault")}</option>
                    {Array.isArray(services) && services.map((s) => (
                      <option key={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t("Contact.form.message")}
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    placeholder={t("Contact.form.messagePlaceholder")}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/40 transition-colors text-sm resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl font-semibold text-white hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/25"
                  style={{ background: "linear-gradient(135deg, #7C3AED, #4F46E5, #06B6D4)" }}
                >
                  {t("Contact.form.submit")}
                </button>
              </form>
            </div>

            {/* Info de contacto */}
            <div className="flex flex-col gap-6">
              {[
                {
                  icon: (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  ),
                  label: t("Contact.info.emailLabel"),
                  value: "info@sis-gt.com",
                  href: "mailto:info@sis-gt.com",
                },
                {
                  icon: (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                  ),
                  label: t("Contact.info.phoneLabel"),
                  value: "+502 5783-4957",
                  href: "tel:+50257834957",
                },
                {
                  icon: (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                  ),
                  label: t("Contact.info.locationLabel"),
                  value: "Condado Almería, Villa Mena 20F, San José Pinula, Guatemala",
                  href: "https://www.google.com/maps/search/?api=1&query=" +
                    encodeURIComponent("Condado Almería, Villa Mena 20F, San José Pinula, Guatemala"),
                  external: true,
                },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  {...(item.external && { target: "_blank", rel: "noopener noreferrer" })}
                  className="flex items-start gap-4 p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all group"
                >
                  <div
                    className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl text-white"
                    style={{ background: "linear-gradient(135deg, #7C3AED, #06B6D4)" }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">{item.label}</p>
                    <p className="text-gray-200 font-medium group-hover:text-cyan-400 transition-colors">
                      {item.value}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────── */}
      <footer className="border-t border-white/10 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src="/img/logo.png" alt="SIS-GT Tecnología" className="h-10 w-auto" />

            <nav className="flex flex-wrap justify-center gap-6">
              {navLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-sm text-gray-500 hover:text-cyan-400 transition-colors"
                >
                  {item.name}
                </a>
              ))}
            </nav>

            <a
              href="mailto:info@sis-gt.com"
              className="text-sm text-gray-500 hover:text-cyan-400 transition-colors"
            >
              info@sis-gt.com
            </a>
          </div>

          <div className="mt-8 pt-8 border-t border-white/5 text-center">
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} SIS-GT Tecnología. {t("Footer.rights")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
